import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { columns } from './Jobs.columns';
import { GET_JOB_IDS, GET_JOBS, JobItem } from './Jobs.query';
import { RouteComponentProps, withRouter } from 'react-router';
import FilterBarJob, {
  defaultFilters,
  JobFilters,
  stringSearchFields,
} from './FilterBarJob';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import useMode from '../../../../hooks/useMode';
import { UserMode } from '../../../../hooks/useApolloClient';
import useTablePages from '../../../../hooks/useTablePages';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import PageBar from '../../../../components/PageBar/PageBar';
import TableWindow from '../../../../components/Table/TableWindow';
import JobsActions from './JobsActions';
import RecycledWaterInspectionThirdPane from '../../../../components/RecycledWater/RecycledWaterInspectionThirdPane';
import { getStringSearchOr } from '../../../../helpers/search';

const convertStoredString = (searchString: string) => ({
  or: getStringSearchOr(stringSearchFields, searchString),
});

const convertStoredDate = (
  dateStart: string | undefined,
  dateEnd: string | undefined,
  currentDateFilterField = 'job.backflowTest.dateTested'
) => {
  return {
    [currentDateFilterField]:
      dateStart && dateEnd
        ? {
            between: [dateStart, dateEnd],
          }
        : dateStart || dateEnd
          ? {
              gte: dateStart ? dateStart : undefined,
              lte: dateEnd ? dateEnd + 'T23:59:59.999Z' : undefined,
            }
          : undefined,
  };
};

export const convertFilters = (storedFilters: JobFilters) => {
  const query: { and: Record<string, any>[] } = { and: [] };
  // if (storedFilters.status) {
  //   query.and.push({
  //     status: storedFilters.status,
  //   })
  // }
  if (storedFilters.stringSearch) {
    query.and.push(convertStoredString(storedFilters.stringSearch));
  }
  if (
    storedFilters.currentDateFilterField !== 'ALL' &&
    (storedFilters.dateStart || storedFilters.dateEnd)
  ) {
    query.and.push(
      convertStoredDate(
        storedFilters.dateStart,
        storedFilters.dateEnd,
        storedFilters.currentDateFilterField
      )
    );
  }

  if (query.and.length === 0) {
    delete (query as any).and;
  }

  return query;
};

type JobDetailParams = {
  id?: string;
};

type JobDetailProps = RouteComponentProps<JobDetailParams>;

/**
 * @component for jobs page
 */
const Jobs: React.FC<JobDetailProps> = ({ match }) => {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<JobFilters>(
      convertFilters,
      'waterSupplierJobs',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy('waterSupplierJobs', columns);

  const mode: UserMode = useMode();

  const { loading, error, itemIds, itemLoader } = useTablePages<JobItem>(
    'jobs',
    GET_JOB_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_JOBS,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
    }),
    'id'
  );

  const [selectedIds, setSelectedIds] = useState([] as readonly string[]);

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(columns, 'storedColumnConfigurationJobs');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${mode}/jobs`, itemIds, match.params.id, 'id');

  return (
    <ThirdPaneLayout paneWidth={384 * 2} paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Jobs" />
        <FilterBarJob
          mb={4}
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<JobsActions assignmentIds={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <RecycledWaterInspectionThirdPane
        jobId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
};

Jobs.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Jobs);
